import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const SessionWarningModal = () => {
  const { showSessionWarning, extendSession, logout } = useAuth();
  const [countdown, setCountdown] = useState(120); // 2 minutos en segundos

  useEffect(() => {
    if (!showSessionWarning) {
      setCountdown(120);
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showSessionWarning, logout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <AnimatePresence>
      {showSessionWarning && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-full max-w-md"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 mx-4">
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <Clock className="w-8 h-8 text-orange-600" />
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Sesión por Expirar
                </h2>

                <p className="text-gray-600 mb-6">
                  Tu sesión se cerrará por inactividad en:
                </p>

                <div className="text-5xl font-bold text-orange-600 mb-8">
                  {formatTime(countdown)}
                </div>

                <div className="flex gap-3 w-full">
                  <motion.button
                    onClick={logout}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <LogOut className="w-5 h-5" />
                    Cerrar Sesión
                  </motion.button>

                  <motion.button
                    onClick={extendSession}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all shadow-lg"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Continuar Activo
                  </motion.button>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  Por seguridad, las sesiones inactivas se cierran automáticamente después de 30 minutos.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SessionWarningModal;
